/**
 * A barra de estado de conexao — `PWA §6.3`.
 *
 * > "UI: barra fina e persistente no topo (**nao toast**), com texto 'Sem conexao — algumas
 * > informacoes podem estar desatualizadas' e, quando volta, 'Conectado' por 2 s.
 * > `role="status"`, `aria-live="polite"` (§32)."
 *
 * ## Por que barra e nao toast
 *
 * Um toast some. "Sem conexao" nao e um evento, e um **estado**: enquanto durar, tudo o que a
 * tela mostra precisa ser lido com essa ressalva. Um toast que sumiu deixa a pessoa olhando um
 * preco antigo sem saber que e antigo.
 *
 * ## O anuncio mora no `<LiveRegions>` da raiz, nao aqui
 *
 * DESIGN_SYSTEM §3.5: componente **nao cria** regiao viva propria. Este componente chama
 * `anunciar()` na mudanca — uma vez — e a barra em si e conteudo visual comum com
 * `role="status"`. Duas regioes `aria-live` competindo fazem o leitor anunciar duas vezes ou
 * nenhuma.
 */

import { Button, useAnnounce } from '@farmacia/ui';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { texto } from '../../conteudo/index.js';
import { usePwa } from '../../pwa/contexto.jsx';

/** "e, quando volta, 'Conectado' por 2 s". */
const DURACAO_DO_RECONECTADO_MS = 2_000;

export interface BarraDeConexaoProps {
  /**
   * `PWA §11.5` — o modo economia liga sozinho "com um aviso discreto e **reversivel**". Sem
   * este gancho o aviso seria so um comunicado, e a pessoa que quer a imagem em alta ficaria
   * sem saida.
   */
  readonly aoDesligarEconomia?: () => void;
}

export function BarraDeConexao({ aoDesligarEconomia }: BarraDeConexaoProps = {}): ReactNode {
  const { conexao } = usePwa();
  const { anunciar } = useAnnounce();
  const anterior = useRef(conexao);
  const [mostrarReconectado, setMostrarReconectado] = useState(false);

  useEffect(() => {
    if (anterior.current === conexao) return;
    const voltouDoOffline = anterior.current === 'OFFLINE';
    anterior.current = conexao;

    if (conexao === 'OFFLINE') {
      anunciar(texto('conexao.offline'), 'status');
      setMostrarReconectado(false);
      return;
    }
    if (voltouDoOffline) {
      anunciar(texto('conexao.reconectado'), 'status');
      setMostrarReconectado(true);
    }
  }, [conexao, anunciar]);

  useEffect(() => {
    if (!mostrarReconectado) return undefined;
    const id = setTimeout(() => { setMostrarReconectado(false); }, DURACAO_DO_RECONECTADO_MS);
    return () => { clearTimeout(id); };
  }, [mostrarReconectado]);

  if (conexao === 'OFFLINE') {
    return (
      <p className="barra-conexao barra-conexao--offline" role="status">
        {texto('conexao.offline')}
      </p>
    );
  }

  if (mostrarReconectado) {
    return (
      <p className="barra-conexao barra-conexao--online" role="status">
        {texto('conexao.reconectado')}
      </p>
    );
  }

  if (conexao === 'DEGRADED') {
    // `PWA §11.5` — o modo economia liga sozinho, "com um aviso discreto e reversivel —
    // **nunca** silenciosamente". Sem o aviso, a pessoa ve imagem pior e acha que o app quebrou.
    return (
      <p className="barra-conexao barra-conexao--economia" role="status">
        {texto('conexao.economia')}{' '}
        {aoDesligarEconomia === undefined ? null : (
          <Button variant="ghost" size="sm" onClick={aoDesligarEconomia}>
            {texto('conexao.economiaDesligar')}
          </Button>
        )}
      </p>
    );
  }

  return null;
}
